<template lang="html">
  <div>
    <div v-if="subscription_data_loaded">
      <div v-if="result">
        <hr />
        <h5 class="mb-3">
          You are currently subscribed to an individual plan
        </h5>
        <p>
          <b>Plan level:</b>
          {{ planNameFromId(user_doc.subscribed_stripe_plan) }}
          <v-icon
            v-if="user_doc.subscribed_stripe_plan == plan.plan_id"
            name="check-circle"
            class="ml-1"
            style="color: green"
          />
        </p>
        <p v-if="result.plan.interval == 'month'">
          Your current monthly membership is valid through
          {{ result.current_period_end | date_string_short }}.
        </p>
        <p v-else>
          Your annual membership is valid through
          {{ result.current_period_end | date_string_short }}.
        </p>
        <p>Renewal: ${{ result.plan.amount / 100 }} USD</p>
      </div>
      <div v-else>
        <h3>You have no active subscription.</h3>
      </div>
    </div>
    <div v-else><b-spinner /></div>

    <div v-if="result" class="mt-4">
      <!-- Disable auto-renewal -->
      <div v-if="!result.cancel_at_period_end" class="">
        <p>
          Your subscription will renew on
          {{ result.current_period_end | date_string_short }}.
        </p>
        <b-button
          type="submit"
          size="sm"
          variant="secondary"
          :disabled="!renewalChangeEnabled"
          @click="setRenewalDisabled(true)"
        >
          Disable renewal<b-spinner
            v-if="!renewalChangeEnabled"
            label="Spinning"
            small
            class="ml-2"
          />
        </b-button>
      </div>

      <!-- Enable auto-renewal -->
      <div v-if="result.cancel_at_period_end" class="">
        <h5>
          <v-icon name="exclamation-triangle" class="mr-2" />Renewal is disabled
          for your account
        </h5>
        <p>
          Your subscription is set to end on
          {{ result.current_period_end | date_string_short }}. At that time,
          your account will convert to an expired trial account.
        </p>
        <b-button
          type="submit"
          size="sm"
          variant="primary"
          :disabled="!renewalChangeEnabled"
          @click="setRenewalDisabled(false)"
        >
          Enable renewal<b-spinner
            v-if="!renewalChangeEnabled"
            label="Spinning"
            small
            class="ml-2"
          />
        </b-button>
      </div>
    </div>

    <div v-if="current_card" class="mt-4">
      <p v-if="current_card.last4">
        Payment source:
        {{ current_card.brand }} ({{ current_card.last4 }}) exp.
        {{ current_card.exp_month }}/{{ current_card.exp_year }}
      </p>
      <div v-if="!current_card.last4">
        <p>Payment source: None.</p>
        <p>
          <v-icon name="exclamation-triangle" /> Please add a new card below.
        </p>
      </div>
      <form :action="changeCardPostUrl" method="post">
        <input
          id="customer_id"
          type="hidden"
          name="customer_id"
          :value="$store.state.user_doc.customer"
        />
        <b-button type="submit" size="sm" variant="secondary">
          Change Card
        </b-button>
      </form>
    </div>

    <div v-if="charges">
      <h5 class="mt-5">Payment history</h5>
      <b-table v-if="charges" :items="charges" :fields="charge_fields">
        <template v-slot:cell(date)="data">
          {{ data.item.created | date_string }}
        </template>
        <template v-slot:cell(product)="data">
          <span v-if="data.item.statement_descriptor">
            {{ data.item.statement_descriptor }}
          </span>
          <span v-else class="text-muted">
            View receipt for info
          </span>
        </template>
        <template v-slot:cell(status)="data">
          <span v-if="data.item.refunded">
            Refunded
          </span>
          <span v-else>
            Paid
          </span>
        </template>

        <template v-slot:cell(total)="data">
          {{ (data.item.amount / 100) | toCurrency }} USD
        </template>
        <template v-slot:cell(receipt)="data">
          <b-btn
            v-if="data.item.status == 'succeeded'"
            :href="data.item.receipt_url"
            target="_blank"
            size="sm"
          >
            View
          </b-btn>
        </template>
      </b-table>
    </div>

    <div v-if="pending_draft_invoices.length > 0">
      <h5 class="mt-5">Payments in-progress</h5>
      <b-table :items="pending_draft_invoices" :fields="pending_draft_fields">
        <template v-slot:cell(date)="data">
          {{ data.item.created | date_string }}
        </template>
        <template v-slot:cell(description)="data">
          {{ data.item.lines.data[0].description }}
        </template>
        <template v-slot:cell(total)="data">
          {{ (data.item.amount_due / 100) | toCurrency }} USD
        </template>
        <template slot="status">
          Pending
        </template>
      </b-table>
      <p class="small">
        Pending payments are processed within 90 minutes of order.
      </p>
    </div>

    <div v-if="past_due_invoices.length > 0">
      <h5 class="mt-5">Payment due</h5>
      <b-table :items="past_due_invoices" :fields="past_due_fields">
        <template v-slot:cell(due)="data">
          {{ data.item.created | date_string }}
        </template>
        <template v-slot:cell(description)="data">
          {{ data.item.lines.data[0].description }}
        </template>
        <template v-slot:cell(total)="data">
          {{ (data.item.amount_due / 100) | toCurrency }} USD
        </template>
        <template v-slot:cell(status)="data">
          <b-btn
            v-if="data.item.status == 'open'"
            :href="data.item.hosted_invoice_url"
            target="_blank"
            size="sm"
          >
            Pay online
          </b-btn>
        </template>
      </b-table>
      <p>
        <v-icon name="exclamation-triangle" /> Payments are due for your
        account.
      </p>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import { stripe_payment_endpoint, plans } from "@/defaults";
import find from "lodash/find";
export default {
  name: "StripeDetails",
  filters: {
    date_string: function(value) {
      value = value * 1000;
      return format(new Date(value), "MMMM d, yyyy h:mm aa");
    },
    date_string_short: function(value) {
      value = value * 1000;
      return format(new Date(value), "MMMM d, yyyy");
    },
    date_string_time: function(value) {
      value = value * 1000;
      return format(new Date(value), "h:mm aa");
    },
    toCurrency: function(value) {
      if (typeof value !== "number") {
        return value;
      }
      var formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2
      });
      return formatter.format(value);
    }
  },
  data() {
    return {
      charge_fields: ["date", "product", "total", "status", "receipt"],
      pending_draft_fields: ["date", "description", "total", "status"],
      past_due_fields: ["due", "description", "total", "status"],
      renewalChangeEnabled: true
    };
  },
  computed: {
    user_doc() {
      return this.$store.state.user_doc;
    },
    plan() {
      return this.$store.getters.user_plan;
    },
    subscription_data_loaded() {
      return "subscriptions" in this.$store.state.stripe_data;
    },
    result() {
      if (this.$store.state.stripe_data.subscriptions) {
        return this.$store.state.stripe_data.subscriptions.data[0];
      }
      return null;
    },
    charges() {
      if (this.$store.state.stripe_data_charges.data) {
        return this.$store.state.stripe_data_charges.data;
      }
      return null;
    },
    pending_draft_invoices() {
      if (this.$store.state.stripe_data_draft_invoices) {
        return this.$store.state.stripe_data_draft_invoices;
      }
      return [];
    },
    past_due_invoices() {
      if (this.$store.state.stripe_data_due_invoices) {
        return this.$store.state.stripe_data_due_invoices;
      }
      return [];
    },
    current_card() {
      if (this.$store.state.stripe_data.default_source) {
        return this.$store.state.stripe_data.sources.data[0];
      } else {
        return null;
      }
    },
    changeCardPostUrl() {
      return `${stripe_payment_endpoint}customer/`;
    },
    disableRenewalPostUrl() {
      return `${stripe_payment_endpoint}customer/renewal/disable/`;
    },
    enableRenewalPostUrl() {
      return `${stripe_payment_endpoint}customer/renewal/enable/`;
    }
  },
  watch: {},
  mounted() {
    this.$store.dispatch("load_stripe_data");
  },
  activated() {
    this.$store.dispatch("load_stripe_data");
  },
  methods: {
    planNameFromId(plan_id) {
      return find(plans, { plan_id: plan_id }).name;
    },
    setRenewalDisabled(disabled) {
      this.renewalChangeEnabled = false;
      var obj = {
        disabled: disabled,
        subscription_id: this.result.id
      };
      this.$store.dispatch("set_stripe_auto_renewal", obj).then(() => {
        console.log("woohoo!");
        this.renewalChangeEnabled = true;
      });
    }
  }
};
</script>

<style lang="css" scoped></style>
